import React from 'react';
import { Link } from 'react-router-dom';
import './ProductCertification.css';

const ProductCertification = () => {
  return (
    <div className="page-container">
      <div className="page-content">
        <div className="page-main">
          <section className="page-section">
            <h1 className="page-title">Product Certification</h1>
            <div className="page-text-content">
              <p>
                Product Certification is a process that verifies that products meet specific 
                standards, regulations, and requirements for safety, quality, and performance. At NQC 
                (Nova Quality Certification Agency), we provide comprehensive product certification 
                services to help businesses ensure their products comply with international standards 
                and gain market access.
              </p>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">What is Product Certification?</h2>
            <div className="page-text-content">
              <p>
                Product Certification involves the testing, inspection, and verification of products 
                to ensure they meet established standards and regulations. This process provides 
                independent confirmation that products are safe, reliable, and compliant with 
                applicable requirements.
              </p>
              <p>
                Product certifications are essential for market access, regulatory compliance, and 
                building consumer trust. They demonstrate that products have been evaluated by 
                independent third parties and meet the necessary quality and safety standards.
              </p>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Benefits of Product Certification</h2>
            <div className="benefits-list">
              <div className="benefit-item">
                <h3 className="benefit-title">1. Market Access</h3>
                <p className="benefit-description">
                  Product certification is often a requirement for entering international markets. 
                  Certified products can be sold in multiple countries and regions, expanding your 
                  business opportunities.
                </p>
              </div>
              <div className="benefit-item">
                <h3 className="benefit-title">2. Regulatory Compliance</h3>
                <p className="benefit-description">
                  Certification ensures your products comply with local and international regulations, 
                  helping you avoid legal issues and penalties.
                </p>
              </div>
              <div className="benefit-item">
                <h3 className="benefit-title">3. Consumer Confidence</h3>
                <p className="benefit-description">
                  Certified products build trust with consumers, who recognize certification marks as 
                  symbols of quality, safety, and reliability.
                </p>
              </div>
              <div className="benefit-item">
                <h3 className="benefit-title">4. Competitive Advantage</h3>
                <p className="benefit-description">
                  Product certification differentiates your products from competitors and demonstrates 
                  your commitment to quality and safety.
                </p>
              </div>
              <div className="benefit-item">
                <h3 className="benefit-title">5. Risk Reduction</h3>
                <p className="benefit-description">
                  Certification helps identify and address potential product issues before they reach 
                  the market, reducing the risk of recalls, liability, and damage to your brand.
                </p>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Our Product Certification Services</h2>
            <div className="page-text-content">
              <p>
                NQC offers a comprehensive range of product certification services:
              </p>
            </div>
            <div className="services-grid">
              <div className="service-card">
                <h3 className="service-card-title">CE Certification</h3>
                <p className="service-card-description">
                  CE marking is mandatory for many products sold in the European Union. Our CE 
                  certification service ensures your products comply with EU directives and 
                  regulations.
                </p>
                <Link to="/ce-certification" className="service-link">Learn More →</Link>
              </div>
              <div className="service-card">
                <h3 className="service-card-title">BRC Certificate</h3>
                <p className="service-card-description">
                  BRC certification demonstrates compliance with food safety and quality standards, 
                  essential for food producers, processors, and retailers.
                </p>
                <Link to="/brc-certificate" className="service-link">Learn More →</Link>
              </div>
              <div className="service-card">
                <h3 className="service-card-title">Kosher Certificate</h3>
                <p className="service-card-description">
                  Kosher certification verifies that products are produced according to Jewish 
                  religious laws, appealing to Jewish consumers and expanding market reach.
                </p>
                <Link to="/kosher-certificate" className="service-link">Learn More →</Link>
              </div>
              <div className="service-card">
                <h3 className="service-card-title">FSSC 22000 Certificate</h3>
                <p className="service-card-description">
                  FSSC 22000 is an internationally recognized food safety certification that combines 
                  ISO 22000 with additional requirements for food safety management.
                </p>
                <Link to="/fssc-22000" className="service-link">Learn More →</Link>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Certification Process</h2>
            <div className="process-list">
              <div className="process-item">
                <h3 className="process-title">1. Application & Documentation</h3>
                <p className="process-description">
                  We begin by reviewing your application and product documentation to understand your 
                  product and certification requirements.
                </p>
              </div>
              <div className="process-item">
                <h3 className="process-title">2. Product Testing</h3>
                <p className="process-description">
                  Products undergo comprehensive testing in accredited laboratories to verify 
                  compliance with applicable standards and regulations.
                </p>
              </div>
              <div className="process-item">
                <h3 className="process-title">3. Factory Inspection</h3>
                <p className="process-description">
                  Our auditors conduct on-site inspections of manufacturing facilities to ensure 
                  production processes meet certification requirements.
                </p>
              </div>
              <div className="process-item">
                <h3 className="process-title">4. Certification Review</h3>
                <p className="process-description">
                  All test results, inspection reports, and documentation are reviewed to determine 
                  certification eligibility.
                </p>
              </div>
              <div className="process-item">
                <h3 className="process-title">5. Certification Issuance</h3>
                <p className="process-description">
                  Upon successful completion, you receive your product certification. We provide 
                  ongoing support and surveillance to maintain certification validity.
                </p>
              </div>
            </div>
          </section>

          <section className="page-section">
            <h2 className="page-section-title">Why Choose NQC?</h2>
            <div className="page-text-content">
              <p>
                NQC (Nova Quality Certification Agency) has extensive experience in product 
                certification across various industries. Our team of experts works closely with you 
                to understand your products and requirements, ensuring a smooth and efficient 
                certification process.
              </p>
              <p>
                We are committed to providing reliable, timely, and cost-effective certification 
                services that help your products succeed in global markets. Contact us today to learn 
                more about our product certification services.
              </p>
            </div>
          </section>
        </div>

        <aside className="page-sidebar">
          <div className="sidebar-section">
            <h2 className="sidebar-title">Our Services</h2>
            <ul className="sidebar-links">
              <li><Link to="/system-certification">System Certification</Link></li>
              <li><Link to="/product-certification">Product Certification</Link></li>
              <li><Link to="/quality-certification">Quality Certification</Link></li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ProductCertification;

